import { Duration } from '@bufbuild/protobuf';
import { acceptHMRUpdate } from 'pinia';
import type {
  Addon,
  OrderLocation,
  Units,
} from 'gportal-grpc/gportal/api/payment/v1/order_pb';
import {
  AddonSelection,
  UnitType,
} from 'gportal-grpc/gportal/api/payment/v1/order_pb';
import {
  CheckoutOrder,
  CreateCheckoutPaymentIntentRequest,
  CreateCheckoutURLRequest,
  GetOrderInformationRequest,
  GetOrderPriceRequest,
} from 'gportal-grpc/gportal/api/payment/v1/requests_pb';
import type {
  AccountBalance,
  AppliedCoupon,
  CreditApplication,
  GetOrderInformationResponse,
  GetOrderPriceResponse,
} from 'gportal-grpc/gportal/api/payment/v1/responses_pb';
import type { Money } from 'gportal-grpc/gportal/type/v1/money_pb';
import type { LocationQuery } from 'vue-router';
import {
  GetCheckoutProduct,
  type GetCheckoutProductQuery,
  type GetCheckoutProductQueryVariables,
  type StrapiCustomComponent,
  type StrapiImage,
} from '~/apollo/types/types';
import {
  CHECKOUT_STORAGE_KEY,
  getUnitSuffix,
  getUnitTypeLabel,
  isCheckoutConfigExpired,
  isSoldOutError,
  REF_COOKIE_NAME,
  SECONDS_PER_DAY,
  stripSelectPrefix,
} from '~/helpers/checkout';
import { GpRegion } from '~/plugins/grpc/universal/types';
import { convertGrpcRegionToGpRegion } from '~/plugins/grpc/universal/utils';

// EUR currencies belong to the EUR region, everything else is INT.
const EUR_CURRENCIES = new Set(['EUR', 'GBP']);

type CheckoutPersistedConfig = {
  planId: string;
  currency: string;
  selectedLocationId: string | null;
  selectedDuration: number | null;
  selectedUnits: number | null;
  selectedAddons?: Record<string, number>; // addon ID → quantity
  couponCodes: string[];
  dismissedAutoCodes: string[];
  repoKey: string | null;
  // Exactly one is set, depending on which Chargebee flow the user came through.
  paymentIntentId: string | null;
  hostedPageId: string | null;
  productKey: string | null;
  // Lets /checkout/result rebuild the GA4 purchase event after the hosted-page flow reloads the page.
  purchaseTotal?: {
    amount: string;
    fraction: number;
    currencyCode: string;
  } | null;
  productName?: string | null;
  timestamp: number;
};

type CheckoutProduct = {
  title: string;
  name: string;
  subHeadline: string;
  slug: string;
  coverImage: StrapiImage | null;
  backgroundImage: StrapiImage | null;
  customComponents: StrapiCustomComponent[];
};

type CheckoutPrice = {
  subtotal: Money;
  addonsTotal: Money;
  discount: Money;
  total: Money;
  convertedTotal?: Money;
  appliedCoupons: AppliedCoupon[];
  // How the logged-in customer's credits would settle `total`. Set only for a
  // balance-migrated customer; a preview from cached balances, the charge is
  // decided at checkout.
  creditApplication?: CreditApplication;
  // The balances that preview is computed from, same condition.
  accountBalance?: AccountBalance;
};

// Repeated params arrive as an array and must not be cast to string.
function firstQueryString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

// Digit-strings only: a fractional value would throw later in BigInt().
function parsePositiveQueryInt(value: unknown): number | null {
  const raw = firstQueryString(value);
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

export const useCheckoutStore = defineStore('checkout', () => {
  // Partner ref link the customer came through. Sent as order metadata so the
  // order is attributed even when the referral coupon is dismissed or does not
  // exist (0 % partner links).
  const refCookie = useCookie<string | number | null>(REF_COOKIE_NAME);
  const planId = ref<string | null>(null);
  const currency = ref('EUR');

  const locations = ref<OrderLocation[]>([]);
  const durations = ref<number[]>([]); // seconds
  const units = ref<Units | null>(null);
  const addons = ref<Addon[]>([]);
  const couponsAvailable = ref(false);
  const complete = ref(false);

  const selectedLocationId = ref<string | null>(null);
  const selectedDuration = ref<number | null>(null);
  const selectedUnits = ref<number | null>(null);
  // Set when the backend rejects the location as belonging to the other region; drives regionMismatch.
  const wrongRegionTarget = ref<GpRegion | null>(null);
  // addon ID -> quantity; flat_fee addons are always 1.
  const selectedAddons = ref<Map<string, number>>(new Map());

  const couponCodes = ref<string[]>([]);

  // Auto-applied coupon codes (reflink + Strapi discounts).
  const autoAppliedCodes = ref<string[]>([]);

  // Codes the user explicitly removed after auto-apply — prevents re-adding on remount.
  const dismissedAutoCodes = ref<string[]>([]);

  const price = ref<CheckoutPrice | null>(null);
  const invalidCoupons = ref<string[]>([]);
  const appliedCoupons = ref<AppliedCoupon[]>([]);

  // Whichever flow the user came through; forwarded to GetCheckoutStatus on the result page.
  const paymentIntentId = ref<string | null>(null);
  const hostedPageId = ref<string | null>(null);

  const product = ref<CheckoutProduct | null>(null);
  const repoKey = ref<string | null>(null);
  const productKey = ref<string | null>(null);

  // Suppresses LocationSelector's best-latency override of a restored choice.
  const restoredFromStorage = ref(false);

  // Suppresses the selectedByDefault auto-select: an explicit choice (even an empty Map) wins.
  const addonsExplicitlyResolved = ref(false);

  const isLoading = ref(false);
  // Ref-counted: a plain boolean dips to false between initFromPlan's two fetches.
  let loadingDepth = 0;
  function beginLoading() {
    loadingDepth += 1;
    isLoading.value = true;
  }
  function endLoading() {
    loadingDepth = Math.max(0, loadingDepth - 1);
    if (loadingDepth === 0) isLoading.value = false;
  }
  const error = ref<string | null>(null);
  const errorCouponCodes = ref<string[]>([]);

  // Backend-flagged sold-out ids, overlaid on the response's `available` flag.
  const soldOutLocationIds = ref<Set<string>>(new Set());

  // Kept separate from `error` so the two never clobber each other.
  const soldOutBanner = ref<string | null>(null);

  function markLocationSoldOut(locationId: string | null | undefined) {
    if (!locationId) return;
    if (locationId === selectedLocationId.value) {
      soldOutBanner.value = t('checkout/configure/location/sold_out_banner');
    }
    if (soldOutLocationIds.value.has(locationId)) return;
    const next = new Set(soldOutLocationIds.value);
    next.add(locationId);
    soldOutLocationIds.value = next;
  }

  // Called from every context-switch path so flags don't leak between plan/currency contexts.
  function clearSoldOutState() {
    soldOutLocationIds.value = new Set();
    soldOutBanner.value = null;
    error.value = null;
  }

  const { t } = useI18n();
  const { pushErrorNotification } = useNotifications();

  const selectedLocationObject = computed(() => {
    if (!selectedLocationId.value) return null;
    return (
      locations.value.find((l) => l.id === selectedLocationId.value) ?? null
    );
  });

  const selectedLocationName = computed<string | null>(
    () => selectedLocationObject.value?.name ?? null,
  );

  // Mirrors LocationSelector's per-card overlay logic so the two can't diverge.
  const selectedLocationSoldOut = computed<boolean>(() => {
    const id = selectedLocationId.value;
    if (!id) return false;
    if (soldOutLocationIds.value.has(id)) return true;
    return selectedLocationObject.value
      ? !selectedLocationObject.value.available
      : false;
  });

  const selectedLocationRegion = computed<GpRegion | null>(() => {
    if (selectedLocationObject.value) {
      return convertGrpcRegionToGpRegion(selectedLocationObject.value.region);
    }
    // A foreign deep-linked location is absent here, so fall back to the region the backend named.
    return wrongRegionTarget.value;
  });

  // Prefer the user's currency when the location allows it, else the location's primary.
  const locationCurrency = computed<string | null>(() => {
    if (!selectedLocationObject.value) return null;
    const locCurrencies = selectedLocationObject.value.currencies;
    if (!locCurrencies || locCurrencies.length === 0) return null;
    if (locCurrencies.includes(currency.value)) return currency.value;
    return locCurrencies[0];
  });

  const summaryLocationLabel = computed<string>(() =>
    stripSelectPrefix(t('checkout/configure/location/title')),
  );

  const summaryDurationLabel = computed<string>(() =>
    stripSelectPrefix(t('checkout/configure/duration/title')),
  );

  const selectedDurationLabel = computed<string | null>(() => {
    if (selectedDuration.value === null) return null;
    const days = Math.round(selectedDuration.value / SECONDS_PER_DAY);
    return t('checkout/configure/duration/days', { count: days });
  });

  const unitTypeLabel = computed<string>(() =>
    getUnitTypeLabel(units.value?.type, t),
  );

  const unitSuffixLabel = computed<string>(() =>
    getUnitSuffix(units.value?.type, t),
  );

  const selectedUnitsLabel = computed<string | null>(() => {
    if (selectedUnits.value === null) return null;
    const suffix = unitSuffixLabel.value;
    let value = selectedUnits.value;
    if (
      units.value?.type === UnitType.SLOT &&
      units.value.multiplier &&
      units.value.multiplier > 0
    ) {
      value *= units.value.multiplier;
    }
    return suffix ? `${value} ${suffix}` : String(value);
  });

  const hasAddons = computed<boolean>(() => {
    if (!price.value?.addonsTotal) return false;
    return Number(price.value.addonsTotal.amount) > 0;
  });

  const hasDiscount = computed<boolean>(() => {
    if (!price.value?.discount) return false;
    return Number(price.value.discount.amount) > 0;
  });

  function persistToStorage() {
    if (!import.meta.client) return;
    // Lets /checkout/result emit the GA4 purchase event after a fresh page load; String() avoids a bigint throw.
    const purchaseTotal = price.value?.total;
    const config: CheckoutPersistedConfig = {
      planId: planId.value ?? '',
      currency: currency.value,
      selectedLocationId: selectedLocationId.value,
      selectedDuration: selectedDuration.value,
      selectedUnits: selectedUnits.value,
      selectedAddons: Object.fromEntries(selectedAddons.value),
      couponCodes: couponCodes.value,
      dismissedAutoCodes: dismissedAutoCodes.value,
      repoKey: repoKey.value,
      paymentIntentId: paymentIntentId.value,
      hostedPageId: hostedPageId.value,
      productKey: productKey.value,
      purchaseTotal: purchaseTotal
        ? {
            amount: String(purchaseTotal.amount),
            fraction: purchaseTotal.fraction,
            currencyCode: purchaseTotal.currencyCode,
          }
        : null,
      productName: product.value?.name || product.value?.title || null,
      timestamp: Date.now(),
    };
    localStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(config));
  }

  function restoreFromStorage(): boolean {
    if (!import.meta.client) return false;
    restoredFromStorage.value = false;
    const raw = localStorage.getItem(CHECKOUT_STORAGE_KEY);
    if (!raw) return false;

    try {
      const config: CheckoutPersistedConfig = JSON.parse(raw);

      if (isCheckoutConfigExpired(config.timestamp)) {
        localStorage.removeItem(CHECKOUT_STORAGE_KEY);
        return false;
      }

      // Only restore if planId matches — drop stale config from a different plan
      if (config.planId !== planId.value) {
        localStorage.removeItem(CHECKOUT_STORAGE_KEY);
        return false;
      }

      currency.value = config.currency;
      selectedLocationId.value = config.selectedLocationId;
      selectedDuration.value = config.selectedDuration;
      selectedUnits.value = config.selectedUnits;
      if (config.selectedAddons) {
        const sanitized = new Map<string, number>();
        for (const [id, qty] of Object.entries(config.selectedAddons)) {
          if (!id) continue;
          const parsed = Math.max(1, Math.floor(Number(qty)));
          if (Number.isNaN(parsed)) continue;
          sanitized.set(id, parsed);
        }
        selectedAddons.value = sanitized;
      }
      couponCodes.value = config.couponCodes;
      dismissedAutoCodes.value = config.dismissedAutoCodes ?? [];
      repoKey.value = config.repoKey ?? null;
      paymentIntentId.value = config.paymentIntentId ?? null;
      hostedPageId.value = config.hostedPageId ?? null;
      productKey.value = config.productKey ?? null;
      restoredFromStorage.value = true;
      // The stored addon choice wins over Chargebee defaults.
      addonsExplicitlyResolved.value = true;

      return true;
    } catch {
      localStorage.removeItem(CHECKOUT_STORAGE_KEY);
      return false;
    }
  }

  function clearStorage() {
    if (!import.meta.client) return;
    localStorage.removeItem(CHECKOUT_STORAGE_KEY);
  }

  // Sequence counters - discard stale responses.
  let orderInfoSeq = 0;
  let priceSeq = 0;

  const PRICE_DISCARDED = '__discarded__';

  // Public RPCs, but a signed-in token lets per-user feature flags resolve; anonymous callers must send no header at all.
  function authMetadataIfLoggedIn(): Headers | undefined {
    const authStore = useAuthStore();

    return authStore.isAuthenticated
      ? authStore.getAuthorizationMetadata()
      : undefined;
  }

  async function fetchPrice(): Promise<string | null> {
    if (!planId.value || !complete.value) return null;

    const seq = ++priceSeq;

    const $grpcPayment = useGrpcPayment();

    const request = new GetOrderPriceRequest({
      planId: planId.value,
      currency: currency.value,
      locationId: selectedLocationId.value ?? undefined,
      duration: selectedDuration.value
        ? new Duration({ seconds: BigInt(selectedDuration.value) })
        : undefined,
      units: selectedUnits.value ?? undefined,
      addons: buildAddonSelections(),
      couponCodes: couponCodes.value,
    });

    const { response: resp, error: err } =
      await $grpcPayment.executeApiCallActive(
        'getOrderPrice',
        request,
        authMetadataIfLoggedIn(),
      );

    // Discard stale response — a newer request supersedes this one.
    if (seq !== priceSeq) return PRICE_DISCARDED;

    if (err) {
      error.value = err.message;
      price.value = null;
      invalidCoupons.value = [];
      errorCouponCodes.value = [];
      pushErrorNotification({
        title: t('general/error/text'),
        message: t('checkout/error/price'),
      });
      return err.message;
    }

    error.value = null;
    const data = resp as unknown as GetOrderPriceResponse;
    price.value = {
      subtotal: data.subtotal!,
      addonsTotal: data.addonsTotal!,
      discount: data.discount!,
      total: data.total!,
      convertedTotal: data.convertedTotal ?? undefined,
      appliedCoupons: data.appliedCoupons ?? [],
      creditApplication: data.creditApplication ?? undefined,
      accountBalance: data.accountBalance ?? undefined,
    };
    invalidCoupons.value = data.invalidCoupons ?? [];
    appliedCoupons.value = data.appliedCoupons ?? [];
    errorCouponCodes.value = [...invalidCoupons.value];
    return null;
  }

  async function fetchOrderInformation(
    options: { omitLocation?: boolean } = {},
  ) {
    if (!planId.value) return;
    beginLoading();
    try {
      await runFetchOrderInformation(options);
    } finally {
      endLoading();
    }
  }

  async function runFetchOrderInformation({
    omitLocation = false,
  }: { omitLocation?: boolean } = {}) {
    const seq = ++orderInfoSeq;

    // Yield a macrotask so rapid-fire calls coalesce into one request.
    await new Promise<void>((r) => {
      setTimeout(r, 0);
    });
    if (seq !== orderInfoSeq) return;

    error.value = null;

    const $grpcPayment = useGrpcPayment();

    const request = new GetOrderInformationRequest({
      planId: planId.value,
      currency: currency.value,
      // Dropping a sold-out selection makes the backend return the full option list.
      locationId: omitLocation
        ? undefined
        : (selectedLocationId.value ?? undefined),
      duration: selectedDuration.value
        ? new Duration({ seconds: BigInt(selectedDuration.value) })
        : undefined,
      units: selectedUnits.value ?? undefined,
    });

    const { response: resp, error: err } =
      await $grpcPayment.executeApiCallActive(
        'getOrderInformation',
        request,
        authMetadataIfLoggedIn(),
      );

    // Discard stale response — a newer request has been dispatched
    if (seq !== orderInfoSeq) return;

    if (err) {
      // Sold-out is an expected inline state; every other error falls through to the toast.
      if (isSoldOutError(err)) {
        markLocationSoldOut(selectedLocationId.value);
        complete.value = false;
        price.value = null;
        // Without options loaded the step would be stuck, so reload without the sold-out location.
        if (locations.value.length === 0) {
          await fetchOrderInformation({ omitLocation: true });
        }
        return;
      }
      error.value = err.message;
      pushErrorNotification({
        title: t('general/error/text'),
        message: t('checkout/error/order_info'),
      });
      return;
    }

    const data = resp as unknown as GetOrderInformationResponse;

    if (data.locations.length > 0) {
      locations.value = data.locations as OrderLocation[];
      // available=true after a refetch is evidence of a restock - unflag it.
      if (soldOutLocationIds.value.size > 0) {
        const next = new Set(soldOutLocationIds.value);
        for (const loc of data.locations) {
          if (loc.available) next.delete(loc.id);
        }
        if (next.size !== soldOutLocationIds.value.size) {
          soldOutLocationIds.value = next;
        }
      }
    }

    // Clear only once the selection is unflagged, so an omitted sold-out location keeps its banner.
    if (!selectedLocationSoldOut.value) {
      soldOutBanner.value = null;
    }

    if (data.durations.length > 0) {
      durations.value = data.durations.map((d: Duration) => Number(d.seconds));
    }
    if (data.units) units.value = data.units as Units;
    addons.value = (data.addons ?? []) as Addon[];
    couponsAvailable.value = data.couponsAvailable;
    complete.value = data.complete;

    // Only auto-select defaults when neither the URL nor storage already decided.
    if (selectedAddons.value.size === 0 && !addonsExplicitlyResolved.value) {
      const defaults = addons.value.filter((a) => a.selectedByDefault);
      for (const addon of defaults) {
        const qty = 1;
        selectedAddons.value.set(addon.id, qty);
      }
      if (defaults.length > 0) {
        selectedAddons.value = new Map(selectedAddons.value);
      }
    }

    if (data.complete) {
      await fetchPrice();
    }

    persistToStorage();
  }

  async function getDataLocale(
    customLocale: string,
    customCurrency: string,
    chargebeePlanId: string,
  ) {
    const { data } = await useLegacyApollo<
      GetCheckoutProductQuery,
      GetCheckoutProductQueryVariables
    >(GetCheckoutProduct, {
      locale: customLocale,
      currency: customCurrency,
      chargebeePlanId: [chargebeePlanId],
    });
    // Several products can share a chargebeePlanId; ?pk= disambiguates.
    if (!data?.products?.length) return null;
    let p = data.products[0];
    if (data.products.length > 1 && productKey.value) {
      const matched = data.products.find(
        (prod) => prod.productKey === productKey.value,
      );
      if (matched) p = matched;
    }
    const customComponents = p.content.filter(
      (c): c is StrapiCustomComponent =>
        c.__typename === 'StrapiCustomComponent',
    );
    return { product: p, customComponents };
  }

  async function fetchProduct(
    chargebeePlanId: string,
    productLocale: string,
    productCurrency: string,
  ) {
    try {
      const result = await getDataLocale(
        productLocale,
        productCurrency,
        chargebeePlanId,
      );
      if (!result) return;
      const p = result.product;
      let customComponents = result.customComponents;

      // Must precede the English fallback, which matches by productKey.
      if (!productKey.value && p.productKey) {
        productKey.value = p.productKey;
      }

      if (customComponents.length === 0 && productLocale !== 'en') {
        const enResult = await getDataLocale(
          'en',
          productCurrency,
          chargebeePlanId,
        );
        if (enResult) customComponents = enResult.customComponents;
      }

      product.value = {
        title: p.title,
        name: p.name,
        subHeadline: p.subHeadline,
        slug: p.slug,
        coverImage: (p.coverImage as StrapiImage) ?? null,
        backgroundImage: (p.backgroundImage as StrapiImage) ?? null,
        customComponents,
      };
    } catch {
      // Product data is non-critical — silently ignore failures
    }
  }

  async function initFromPlan(
    newPlanId: string,
    newCurrency: string,
    force = false,
  ) {
    // Already initialized for this exact plan+currency — nothing to do.
    if (
      !force &&
      planId.value === newPlanId &&
      currency.value === newCurrency &&
      locations.value.length > 0
    ) {
      return;
    }
    // Spans both inner fetches so sections don't flicker between them.
    beginLoading();
    try {
      await runInitFromPlan(newPlanId, newCurrency, force);
    } finally {
      endLoading();
    }
  }

  async function runInitFromPlan(
    newPlanId: string,
    newCurrency: string,
    force: boolean,
  ) {
    // Wipe selections when the plan changes, but never on a fresh visit - that would clobber the URL's params.
    if (planId.value !== null && planId.value !== newPlanId) {
      paymentIntentId.value = null;
      hostedPageId.value = null;
      selectedLocationId.value = null;
      selectedDuration.value = null;
      selectedUnits.value = null;
      selectedAddons.value = new Map();
      couponCodes.value = [];
      locations.value = [];
      durations.value = [];
      units.value = null;
      addons.value = [];
      complete.value = false;
      price.value = null;
      invalidCoupons.value = [];
      appliedCoupons.value = [];
      product.value = null;
      repoKey.value = null;
      // New plan: let this plan's selectedByDefault addons apply.
      addonsExplicitlyResolved.value = false;
      // Sold-out flags are per (plan, currency, location).
      clearSoldOutState();
    } else if (currency.value !== newCurrency) {
      // Keep locations so LocationSelector stays mounted and preserves the user's pick across the switch.
      paymentIntentId.value = null;
      hostedPageId.value = null;
      selectedDuration.value = null;
      selectedUnits.value = null;
      durations.value = [];
      units.value = null;
      complete.value = false;
      price.value = null;
      invalidCoupons.value = [];
      appliedCoupons.value = [];
      clearSoldOutState();
    } else if (force) {
      // Caller-forced re-init: clear stale pricing.
      complete.value = false;
      price.value = null;
      clearSoldOutState();
    }
    planId.value = newPlanId;
    currency.value = newCurrency;
    await fetchOrderInformation();

    // Pre-seed so component watchers skip their auto-select and don't cascade fetches.
    if (selectedLocationId.value === null && locations.value.length > 0) {
      // Overlay-aware so a backend-flagged id is never auto-picked.
      const available = locations.value.filter(
        (l) => l.available && !soldOutLocationIds.value.has(l.id),
      );
      if (available.length > 0) selectedLocationId.value = available[0].id;
    }
    if (selectedDuration.value === null && durations.value.length > 0) {
      const thirtyDay = durations.value.find((d) => d === 30 * SECONDS_PER_DAY);
      selectedDuration.value = thirtyDay ?? durations.value[0];
    }
    if (selectedUnits.value === null && units.value?.default !== undefined) {
      selectedUnits.value = units.value.default;
    }

    // Second fetch with all selections so the backend returns complete=true.
    // Skip when the first fetch already returned complete (all selections
    // were provided upfront, e.g. from query params).
    if (
      !complete.value &&
      (selectedLocationId.value ||
        selectedDuration.value ||
        selectedUnits.value)
    ) {
      await fetchOrderInformation();
    }
  }

  // Any order-shape change invalidates a created checkout; persist now because a later fetch can early-return.
  function invalidateCheckoutIds() {
    paymentIntentId.value = null;
    hostedPageId.value = null;
    persistToStorage();
  }

  async function selectLocation(locationId: string) {
    if (selectedLocationId.value === locationId) return;
    invalidateCheckoutIds();
    selectedLocationId.value = locationId;
    // A fresh location choice clears any prior cross-region rejection.
    wrongRegionTarget.value = null;
    complete.value = false;

    await fetchOrderInformation();

    if (
      selectedDuration.value !== null &&
      durations.value.length > 0 &&
      !durations.value.includes(selectedDuration.value)
    ) {
      const thirtyDay = durations.value.find((d) => d === 30 * SECONDS_PER_DAY);
      selectedDuration.value = thirtyDay ?? durations.value[0];
    } else if (durations.value.length === 0) {
      selectedDuration.value = null;
    }
  }

  async function selectDuration(durationSeconds: number) {
    if (selectedDuration.value === durationSeconds) return;
    invalidateCheckoutIds();
    selectedDuration.value = durationSeconds;
    complete.value = false;

    await fetchOrderInformation();
  }

  async function selectUnits(unitCount: number) {
    if (selectedUnits.value === unitCount) return;
    invalidateCheckoutIds();
    selectedUnits.value = unitCount;
    complete.value = false;
    await fetchOrderInformation();
  }

  async function toggleAddon(addonId: string) {
    invalidateCheckoutIds();
    if (selectedAddons.value.has(addonId)) {
      selectedAddons.value.delete(addonId);
    } else {
      const addon = addons.value.find((a) => a.id === addonId);
      const qty = 1;
      selectedAddons.value.set(addonId, qty);
    }
    // Trigger reactivity for the Map.
    selectedAddons.value = new Map(selectedAddons.value);
    if (complete.value) {
      await fetchPrice();
    }
    persistToStorage();
  }

  async function setAddonQuantity(addonId: string, quantity: number) {
    if (!selectedAddons.value.has(addonId)) return;
    if (!Number.isFinite(quantity)) return;
    invalidateCheckoutIds();
    selectedAddons.value.set(addonId, Math.max(1, Math.floor(quantity)));
    selectedAddons.value = new Map(selectedAddons.value);
    if (complete.value) {
      await fetchPrice();
    }
    persistToStorage();
  }

  async function addCoupon(code: string): Promise<boolean> {
    const normalized = code.trim().toUpperCase();
    if (!normalized || couponCodes.value.includes(normalized)) return true;
    invalidateCheckoutIds();
    couponCodes.value.push(normalized);
    if (complete.value) {
      const priceResult = await fetchPrice();
      if (priceResult === PRICE_DISCARDED) {
        // A newer fetchPrice superseded ours — it will reconcile validity.
        persistToStorage();
        return true;
      }
      if (priceResult) {
        // Coupon-caused price error: clear the global error, null the price so Continue disables.
        error.value = null;
        price.value = null;
        errorCouponCodes.value.push(normalized);
        persistToStorage();
        return true;
      }
    }
    persistToStorage();
    const isValid = !invalidCoupons.value.includes(normalized);
    if (!isValid && !errorCouponCodes.value.includes(normalized)) {
      errorCouponCodes.value.push(normalized);
    }
    return isValid;
  }

  async function removeCoupon(code: string) {
    const normalized = code.trim().toUpperCase();
    if (couponCodes.value.includes(normalized)) {
      invalidateCheckoutIds();
    }
    couponCodes.value = couponCodes.value.filter((c) => c !== normalized);
    errorCouponCodes.value = [];

    if (
      autoAppliedCodes.value.includes(normalized) &&
      !dismissedAutoCodes.value.includes(normalized)
    ) {
      dismissedAutoCodes.value.push(normalized);
    }

    if (complete.value) {
      await fetchPrice();
    }
    error.value = null;
    persistToStorage();
  }

  function initFromQueryParams(query: LocationQuery) {
    // The URL is authoritative: a missing param means reset, not preserve.
    selectedLocationId.value = firstQueryString(query.loc);
    selectedDuration.value = parsePositiveQueryInt(query.dur);
    selectedUnits.value = parsePositiveQueryInt(query.units);
    const addonsQuery = firstQueryString(query.addons);
    if (addonsQuery) {
      const entries = addonsQuery.split(',').map((part) => {
        const [id, qtyStr] = part.split(':');
        const qty = qtyStr ? Number(qtyStr) : 1;
        return [id, qty] as [string, number];
      });
      selectedAddons.value = new Map(entries);
    } else {
      selectedAddons.value = new Map();
    }
    const couponQuery = firstQueryString(query.coupon);
    if (couponQuery)
      couponCodes.value = couponQuery
        .split(',')
        .map((c) => c.trim().toUpperCase());
    const repoKeyQuery = firstQueryString(query.repo_key);
    if (repoKeyQuery) repoKey.value = repoKeyQuery;
    const productKeyQuery = firstQueryString(query.pk);
    if (productKeyQuery) {
      productKey.value = productKeyQuery;
      persistToStorage();
    }

    // Only a URL-pinned location suppresses best-latency.
    restoredFromStorage.value = Boolean(query.loc);

    addonsExplicitlyResolved.value = true;
  }

  function toQueryParams(): Record<string, string> {
    const params: Record<string, string> = {};
    if (selectedLocationId.value) params.loc = selectedLocationId.value;
    if (selectedDuration.value) params.dur = String(selectedDuration.value);
    if (selectedUnits.value) params.units = String(selectedUnits.value);
    if (selectedAddons.value.size > 0) {
      params.addons = [...selectedAddons.value.entries()]
        .map(([id, qty]) => `${id}:${qty}`)
        .join(',');
    }
    if (couponCodes.value.length) params.coupon = couponCodes.value.join(',');
    if (productKey.value) params.pk = productKey.value;
    return params;
  }

  function buildAddonSelections(): AddonSelection[] {
    return [...selectedAddons.value.entries()].map(
      ([addonId, quantity]) => new AddonSelection({ addonId, quantity }),
    );
  }

  function buildCheckoutMetadata(): Record<string, string> {
    const metadata: Record<string, string> = {};
    if (selectedLocationId.value) {
      // eslint-disable-next-line camelcase
      metadata.location_key = selectedLocationId.value;
    }
    if (repoKey.value) {
      // eslint-disable-next-line camelcase
      metadata.repo_key = repoKey.value;
    }
    if (productKey.value) {
      // eslint-disable-next-line camelcase
      metadata.product_key = productKey.value;
    }
    const referenceId =
      refCookie.value != null ? String(refCookie.value).trim() : '';
    if (referenceId) {
      // eslint-disable-next-line camelcase
      metadata.reference_id = referenceId;
    }
    return metadata;
  }

  // Single source for the CheckoutOrder proto shared by both checkout flows.
  function buildCheckoutOrder(): CheckoutOrder {
    return new CheckoutOrder({
      planId: planId.value ?? '',
      currency: currency.value,
      duration: selectedDuration.value
        ? new Duration({ seconds: BigInt(selectedDuration.value) })
        : undefined,
      units: selectedUnits.value ?? undefined,
      metadata: buildCheckoutMetadata(),
      addons: buildAddonSelections(),
      couponCodes: couponCodes.value,
    });
  }

  function toCreateCheckoutURLRequest(): CreateCheckoutURLRequest {
    return new CreateCheckoutURLRequest({ order: buildCheckoutOrder() });
  }

  function toCreateCheckoutPaymentIntentRequest(): CreateCheckoutPaymentIntentRequest {
    return new CreateCheckoutPaymentIntentRequest({
      order: buildCheckoutOrder(),
    });
  }

  // Current region: the active region when authenticated, else inferred from the currency.
  function currentRegion(): GpRegion {
    const authStore = useAuthStore();
    if (authStore.isAuthenticated && authStore.activeRegion) {
      return authStore.activeRegion;
    }
    return EUR_CURRENCIES.has(currency.value) ? GpRegion.EUR : GpRegion.INT;
  }

  const regionMismatch = computed(() => {
    if (!selectedLocationRegion.value) return false;
    return selectedLocationRegion.value !== currentRegion();
  });

  // Lights up the region-mismatch UI; falls back to the opposite region when the error named none.
  function flagWrongRegion(target: GpRegion | null) {
    wrongRegionTarget.value =
      target ??
      (currentRegion() === GpRegion.EUR ? GpRegion.INT : GpRegion.EUR);
  }

  // Clears selections but keeps plan identity and server data so watchers re-apply defaults.
  function resetConfigSelections() {
    selectedLocationId.value = null;
    wrongRegionTarget.value = null;
    selectedDuration.value = null;
    selectedUnits.value = null;
    selectedAddons.value = new Map();
    couponCodes.value = [];
    autoAppliedCodes.value = [];
    dismissedAutoCodes.value = [];
    complete.value = false;
    price.value = null;
    invalidCoupons.value = [];
    appliedCoupons.value = [];
    paymentIntentId.value = null;
    hostedPageId.value = null;
    repoKey.value = null;
    productKey.value = null;
    restoredFromStorage.value = false;
    addonsExplicitlyResolved.value = false;
    errorCouponCodes.value = [];
    clearSoldOutState();
    clearStorage();

    // Apply defaults if data already loaded; no-op on first call.
    if (locations.value.length > 0) {
      const available = locations.value.filter((l) => l.available);
      if (available.length > 0) selectedLocationId.value = available[0].id;
    }
    if (durations.value.length > 0) {
      const thirtyDay = durations.value.find((d) => d === 30 * SECONDS_PER_DAY);
      selectedDuration.value = thirtyDay ?? durations.value[0];
    }
    if (units.value?.default !== undefined) {
      selectedUnits.value = units.value.default;
    }
  }

  function reset() {
    planId.value = null;
    currency.value = 'EUR';
    locations.value = [];
    durations.value = [];
    units.value = null;
    addons.value = [];
    couponsAvailable.value = false;
    complete.value = false;
    selectedLocationId.value = null;
    selectedDuration.value = null;
    selectedUnits.value = null;
    selectedAddons.value = new Map();
    couponCodes.value = [];
    autoAppliedCodes.value = [];
    dismissedAutoCodes.value = [];
    price.value = null;
    invalidCoupons.value = [];
    appliedCoupons.value = [];
    paymentIntentId.value = null;
    hostedPageId.value = null;
    product.value = null;
    repoKey.value = null;
    productKey.value = null;
    restoredFromStorage.value = false;
    addonsExplicitlyResolved.value = false;
    errorCouponCodes.value = [];
    // Bump, never zero: an in-flight request must not land in the reset store.
    orderInfoSeq += 1;
    priceSeq += 1;
    clearSoldOutState();
  }

  return {
    planId,
    currency,
    locations,
    durations,
    units,
    addons,
    couponsAvailable,
    complete,
    selectedLocationId,
    selectedDuration,
    selectedUnits,
    selectedAddons,
    couponCodes,
    autoAppliedCodes,
    dismissedAutoCodes,
    price,
    invalidCoupons,
    appliedCoupons,
    paymentIntentId,
    hostedPageId,
    errorCouponCodes,
    product,
    repoKey,
    productKey,
    restoredFromStorage,
    isLoading,
    error,
    soldOutLocationIds,
    soldOutBanner,

    selectedLocationObject,
    selectedLocationName,
    selectedLocationSoldOut,
    selectedLocationRegion,
    locationCurrency,
    summaryLocationLabel,
    summaryDurationLabel,
    selectedDurationLabel,
    unitTypeLabel,
    unitSuffixLabel,
    selectedUnitsLabel,
    hasAddons,
    hasDiscount,
    regionMismatch,
    wrongRegionTarget,

    fetchProduct,
    initFromPlan,
    resetConfigSelections,
    markLocationSoldOut,
    flagWrongRegion,
    selectLocation,
    selectDuration,
    selectUnits,
    toggleAddon,
    setAddonQuantity,
    addCoupon,
    removeCoupon,
    fetchOrderInformation,
    fetchPrice,
    persistToStorage,
    restoreFromStorage,
    clearStorage,
    initFromQueryParams,
    toQueryParams,
    toCreateCheckoutURLRequest,
    toCreateCheckoutPaymentIntentRequest,
    reset,
  };
});

// Pinia HMR: re-bind the live store instance to updated functions on edit.
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCheckoutStore, import.meta.hot));
}
